import React, { useState } from "react";
import { ExternalLink, ShieldCheck, CreditCard, CalendarCheck } from "lucide-react";
import Seo, { breadcrumbSchema } from "../components/Seo";
import { Reveal, Eyebrow, PageHero, BtnPrimary, BtnGhost, CtaBand, Crumbs } from "../components/Shared";
import { IMG, CHALKIT_URL, CHALKIT_PRODUCTS } from "../data/mock";
import { trackGoalReviewBooking } from "../lib/analytics";

export default function Book() {
  const [frameLoaded, setFrameLoaded] = useState(false);

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Book Online", path: "/book" },
  ];

  return (
    <>
      <Seo
        title="Book Online via ChalkIt Pro | Boston Iron Grit"
        description="Buy memberships, class packs and drop-ins at Boston Iron Grit securely through ChalkIt Pro. Month-to-month plans with instant confirmation."
        path="/book"
        schemas={[breadcrumbSchema(crumbs)]}
      />
      <PageHero
        label="Book Online"
        title={<>Join in under<br /><span className="text-neutral-500">two minutes.</span></>}
        sub="Memberships, class packs and drop-ins are handled securely through ChalkIt Pro — our booking and membership engine. Buy below or open it in a new tab."
        image={IMG.kettlebell}
      />

      <section className="bg-[#0A0A0B] pb-6">
        <div className="max-w-7xl mx-auto px-6">
          <Crumbs items={crumbs} />
        </div>
      </section>

      {/* Trust row */}
      <section className="bg-[#0A0A0B] py-8">
        <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-3 gap-px bg-white/5 border border-white/5">
          {[
            { icon: ShieldCheck, t: "Secure Checkout", d: "Payments processed by ChalkIt Pro — we never see your card details." },
            { icon: CreditCard, t: "Month-to-Month", d: "No contracts, no initiation fees. Pause or cancel anytime." },
            { icon: CalendarCheck, t: "Instant Access", d: "Book your first class the moment your purchase confirms." },
          ].map((f) => (
            <div key={f.t} className="bg-[#0D0D0F] p-6 flex items-start gap-4">
              <f.icon size={20} className="text-[#9D0423] mt-0.5 shrink-0" />
              <div>
                <h2 className="font-heading font-bold uppercase text-base text-white tracking-tight">{f.t}</h2>
                <p className="mt-1.5 text-[13px] text-neutral-400 leading-relaxed">{f.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Free Goal Review — direct Chalk It Pro booking */}
      <section className="bg-[#0A0A0B] py-10">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="border border-[#9D0423]/40 bg-[#121214] p-8 lg:p-10 grid lg:grid-cols-[1fr_auto] gap-8 items-center" data-testid="book-goal-review-option">
              <div>
                <Eyebrow>New Here? Start Free</Eyebrow>
                <h2 className="font-heading font-extrabold uppercase text-3xl text-white tracking-tight">Free Goal Review</h2>
                <p className="mt-3 text-neutral-300 text-[15px] leading-relaxed max-w-xl">
                  A free 20-minute conversation with a coach about your goals, training history and the best place to start.
                  Pick an available time, complete the required waiver and you're on the schedule — no credit card required.
                </p>
              </div>
              <a
                href={CHALKIT_PRODUCTS.goalReview}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="book-page-goal-review-button"
                onClick={() => trackGoalReviewBooking("book-page-option", CHALKIT_PRODUCTS.goalReview)}
                className="inline-flex items-center justify-center gap-2 bg-[#9D0423] hover:bg-[#7E031C] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white text-white font-mono font-semibold text-[13px] uppercase tracking-[0.14em] px-7 py-4 transition-colors whitespace-nowrap"
              >
                Book Your Free Goal Review
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Embedded ChalkIt Pro */}
      <section className="bg-[#0A0A0B] py-10 lg:py-14">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <Eyebrow>Powered by ChalkIt Pro</Eyebrow>
                <h2 className="font-heading font-extrabold uppercase text-3xl text-white tracking-tight">Choose your plan</h2>
              </div>
              <a
                href={CHALKIT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-[0.14em] text-neutral-300 hover:text-white border border-white/15 hover:border-white/40 px-5 py-3 transition-colors"
              >
                Open in New Tab <ExternalLink size={13} />
              </a>
            </div>
            <div className="relative border border-white/10 bg-[#0D0D0F]">
              {!frameLoaded && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-10">
                  <div className="w-10 h-10 border-2 border-white/15 border-t-[#9D0423] rounded-full animate-spin" />
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">Loading ChalkIt Pro…</p>
                  <p className="text-[12px] text-neutral-600 max-w-sm text-center px-6">
                    If the booking widget doesn't appear, your browser may be blocking the embed — use the "Open in New Tab" button above.
                  </p>
                </div>
              )}
              <iframe
                title="ChalkIt Pro — Boston Iron Grit memberships and booking"
                src={CHALKIT_URL}
                className="relative w-full h-[900px] z-20"
                style={{ border: 0, background: "transparent" }}
                onLoad={() => setFrameLoaded(true)}
                allow="payment"
              />
            </div>
            <p className="mt-4 text-[12px] text-neutral-600">
              Trouble with the embedded checkout? <a href={CHALKIT_URL} target="_blank" rel="noopener noreferrer" className="text-[#E56A79] hover:text-white transition-colors">Open ChalkIt Pro directly</a> — same products, same secure checkout.
            </p>
          </Reveal>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <BtnPrimary to="/get-started">Not Sure Yet? Free Goal Review</BtnPrimary>
            <BtnGhost to="/pricing">Compare Plans First</BtnGhost>
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
